import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { FieldsetModule } from 'primeng/fieldset';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import { NuevoEncabezadoFacturaComponent } from './nuevo-encabezado-factura.component';
import {
  ModificarEncabezadoFacturaComponent,
  ValidarNombreFacturaEvent
} from './modificar-encabezado-factura.component';
import {
  BusquedaAseguradoraDialogComponent,
  AseguradoraSeleccionada
} from '../shared/buscadores/buscador-aseguradora-dialog.component';
import { BuscadorConvenioDialogComponent } from '../shared/buscadores/buscador-convenio-dialog.component';
import {
  AsesorSeleccionado,
  BusquedaAsesorDialogComponent
} from '../common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from '../common/busquedas/busqueda-cliente-dialog.component';
import { BuscadorMunicipioComponent } from '../shared/buscadores/buscador-municipio.component';
import { FacturasResultadosComponent } from './components/facturas-resultados.component';
import { ConvenioSeleccionado } from 'src/app/domain/model/convenio/convenio';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { environment } from 'src/environments/environment';
import { NO_VALUE, TIPO_CITA, YES_VALUE } from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';
import {
  ActualizarEncabezadoFacturaPayload,
  EncabezadoFactura,
  FacturaDetallePrefactura,
  FacturaEncabezado,
  TransporteProveedor
} from 'src/app/domain/model/servicios-entregados/factura-encabezado.model';
import { ServiciosEntregadosFacade } from './services/servicios-entregados.facade';
import { RectificarTransporteComponent } from './rectificar-transporte.component';

type CampoNumericoDetalle =
  | 'nmcantidadEjecutada'
  | 'ptvalorTransporte'
  | 'ptvalorTransporteAdicional'
  | 'ptvalorAdicionalCons'
  | 'ptvalorViaticos';

type CampoTextoDetalle =
  | 'dsproveedorTransporte'
  | 'dsespecialTransporte'
  | 'dsvaletaxiTransporte'
  | 'dsobservacionViaticos';

@Component({
  selector: 'app-servicios-entregados',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    FieldsetModule,
    CheckboxModule,
    LoadingOverlayComponent,
    NuevoEncabezadoFacturaComponent,
    ModificarEncabezadoFacturaComponent,
    BusquedaAseguradoraDialogComponent,
    BuscadorConvenioDialogComponent,
    BusquedaAsesorDialogComponent,
    BusquedaClienteDialogComponent,
    BuscadorMunicipioComponent,
    FacturasResultadosComponent,
    RectificarTransporteComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [MessageService, ConfirmationService, ServiciosEntregadosFacade],
  template: `
    <div class="p-4 max-w-full mx-auto">
      <p-toast></p-toast>
      <p-confirmDialog></p-confirmDialog>

      <app-loading-overlay [blocked]="loading()"></app-loading-overlay>

      <h1 class="text-2xl font-bold mb-4">Servicios Entregados</h1>

      <p-fieldset
        legend="Parámetros de búsqueda"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
        >
          <div>
            <label
              for="cdfactura"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Número de Factura</label
            >
            <input
              pInputText
              id="cdfactura"
              [(ngModel)]="filtro.cdfactura"
              (input)="onCdFacturaInput()"
              placeholder="Ej: FAC-001"
              class="w-full"
              maxlength="20"
            />
          </div>

          <div>
            <label
              for="mes"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Mes</label
            >
            <p-dropdown
              id="mes"
              [options]="mesesOptions"
              [(ngModel)]="filtro.mes"
              placeholder="Seleccione mes"
              [showClear]="true"
              optionLabel="label"
              optionValue="value"
              styleClass="w-full"
            ></p-dropdown>
          </div>

          <div>
            <label
              for="anio"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Año</label
            >
            <input
              pInputText
              id="anio"
              type="number"
              [(ngModel)]="filtro.anio"
              (input)="onAnioInput()"
              placeholder="Ej: 2026"
              class="w-full"
              min="1900"
              max="9999"
            />
          </div>

          <div class="col-span-full lg:col-span-2">
            <label
              for="cliente"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Aseguradora</label
            >
            <div class="flex gap-1 min-w-0">
              <input
                pInputText
                id="clienteCodigo"
                [value]="clienteCodigoLabel()"
                placeholder="Código"
                readonly
                class="w-28 min-w-0 cursor-pointer"
                pTooltip="Buscar aseguradora"
                tooltipPosition="top"
                (click)="abrirBuscadorCliente()"
              />
              <input
                pInputText
                id="clienteIdentificacion"
                [value]="clienteIdentificacionLabel()"
                placeholder="Identificación"
                readonly
                class="w-48 min-w-0 cursor-pointer"
                pTooltip="Buscar aseguradora"
                tooltipPosition="top"
                (click)="abrirBuscadorCliente()"
              />
              <input
                pInputText
                id="cliente"
                [value]="clienteNombreLabel()"
                placeholder="Seleccione una aseguradora a la cual se genera la prefactura..."
                readonly
                class="flex-1 min-w-0 cursor-pointer"
                pTooltip="Buscar aseguradora"
                tooltipPosition="top"
                (click)="abrirBuscadorCliente()"
              />
              <button
                class="min-w-[44px] min-h-[44px] p-button-text flex-shrink-0"
                pButton
                icon="pi pi-search"
                pTooltip="Buscar aseguradora"
                (click)="abrirBuscadorCliente()"
              >
                <span class="sr-only">Buscar aseguradora</span>
              </button>
              @if (filtro.nmconsecutivoCliente) {
                <button
                  class="min-w-[44px] min-h-[44px] p-button-text p-button-danger flex-shrink-0"
                  pButton
                  icon="pi pi-times"
                  pTooltip="Limpiar"
                  (click)="limpiarCliente()"
                >
                  <span class="sr-only">Limpiar aseguradora</span>
                </button>
              }
            </div>
          </div>

          <div>
            <label
              for="oficina"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Oficina</label
            >
            <p-dropdown
              id="oficina"
              [options]="oficinas()"
              [(ngModel)]="filtro.nmconsecutivoOficina"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              placeholder="Seleccione oficina"
              [showClear]="true"
              [filter]="true"
              filterBy="dsoficina"
              styleClass="w-full"
            ></p-dropdown>
          </div>

          <div class="col-span-full lg:col-span-3">
            <label
              for="convenio"
              class="block text-sm font-medium text-gray-700 mb-1"
              >Convenio</label
            >
            <div class="flex gap-2">
              <input
                pInputText
                id="nombreConvenio"
                [(ngModel)]="filtro.nombreConvenio"
                placeholder="Seleccione un convenio"
                readonly
                class="flex-1 cursor-pointer"
                pTooltip="Buscar Convenio"
                tooltipPosition="top"
                (click)="abrirBuscadorConvenio()"
              />
              <button
                pButton
                icon="pi pi-search"
                (click)="abrirBuscadorConvenio()"
                pTooltip="Buscar Convenio"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
              >
                <span class="sr-only">Buscar convenio</span>
              </button>
              @if (filtro.nmconsecutivoConvenio) {
                <button
                  class="min-w-[44px] min-h-[44px] p-button-text p-button-danger flex-shrink-0"
                  pButton
                  icon="pi pi-times"
                  pTooltip="Limpiar"
                  (click)="limpiarConvenio()"
                >
                  <span class="sr-only">Limpiar convenio</span>
                </button>
              }
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 mt-4">
          <button
            pButton
            type="button"
            label="Nuevo Encabezado"
            icon="pi pi-plus"
            class="p-button-outlined p-button-sm p-button-success"
            (click)="mostrarNuevoEncabezadoFactura.set(true)"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            type="button"
            label="Buscar"
            icon="pi pi-search"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            type="button"
            label="Limpiar"
            icon="pi pi-times"
            class="p-button-secondary"
            (click)="limpiarFiltros()"
            [disabled]="loading()"
          ></button>
        </div>
      </p-fieldset>

      @if (mostrarBuscadorCliente()) {
        <app-busqueda-aseguradora-dialog
          (aseguradoraSelected)="onAseguradoraSeleccionada($event)"
          (dialogClosed)="onCerrarBusquedaCliente()"
        ></app-busqueda-aseguradora-dialog>
      }

      @if (mostrarBuscadorConvenio()) {
        <app-busqueda-convenio-dialog
          [visible]="true"
          (visibleChange)="!$event && mostrarBuscadorConvenio.set(false)"
          [nmconsecutivoCliente]="filtro.nmconsecutivoCliente"
          [nombreCliente]="filtro.nombreCliente"
          (convenioSeleccionado)="onConvenioSeleccionado($event)"
        ></app-busqueda-convenio-dialog>
      }

      <p-dialog
        header="Nuevo Encabezado de Servicios Entregados"
        [visible]="mostrarNuevoEncabezadoFactura()"
        (visibleChange)="mostrarNuevoEncabezadoFactura.set($event)"
        (onHide)="mostrarNuevoEncabezadoFactura.set(false)"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [maximizable]="true"
        [style]="{ width: '85vw', maxWidth: '1200px' }"
        [contentStyle]="{ overflow: 'auto', maxHeight: '80vh' }"
      >
        <app-nuevo-encabezado-factura
          (encabezadoCreado)="onEncabezadoCreado($event)"
        ></app-nuevo-encabezado-factura>
      </p-dialog>

      <p-dialog
        header="Modificar Encabezado Servicios Entregados"
        [visible]="mostrarModificarEncabezadoFactura()"
        (visibleChange)="mostrarModificarEncabezadoFactura.set($event)"
        (onHide)="cerrarDialogoModificar()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{ width: '60vw', maxWidth: '900px' }"
        [contentStyle]="{ overflow: 'auto', maxHeight: '80vh' }"
      >
        <app-modificar-encabezado-factura
          [factura]="facturaSeleccionadaEditar()"
          [loading]="loading()"
          (cancelar)="cerrarDialogoModificar()"
          (guardar)="guardarEdicion($event)"
          (validarNombre)="onValidarNombreEdicion($event)"
        ></app-modificar-encabezado-factura>
      </p-dialog>

      <p-dialog
        #detalleDialog
        header="Detalle Servicios Entregados"
        [visible]="mostrarDetalleFactura()"
        (visibleChange)="mostrarDetalleFactura.set($event)"
        (onHide)="cerrarDialogoDetalle()"
        (onShow)="maximizarDialogoDetalle(detalleDialog)"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [maximizable]="true"
        [closable]="false"
        [closeOnEscape]="false"
        [dismissableMask]="false"
        [style]="{ width: '92vw', maxWidth: '1400px' }"
        [contentStyle]="{ overflow: 'auto', maxHeight: '80vh' }"
      >
        @if (facturaSeleccionadaDetalle(); as facturaDetalle) {
          <div class="flex flex-col gap-4">
            <!-- Barra de acciones (legacy: orden Regresar → ... → Grabar) -->
            <div
              class="flex flex-wrap items-center justify-end gap-3 pb-2 border-b border-gray-200 dark:border-gray-700"
            >
              <p-button
                label="Regresar"
                icon="pi pi-arrow-left"
                [outlined]="true"
                severity="secondary"
                size="small"
                (onClick)="intentarCerrarDialogoDetalle()"
              ></p-button>
              <p-button
                label="Rectificar Transporte"
                icon="pi pi-truck"
                [outlined]="true"
                severity="secondary"
                size="small"
                (onClick)="irRectificarTransporte()"
              ></p-button>
              <p-button
                label="Entregado Consultor"
                icon="pi pi-check-circle"
                [outlined]="true"
                severity="secondary"
                size="small"
                [disabled]="true"
              ></p-button>
              <p-button
                label="Transporte Consultores"
                icon="pi pi-car"
                [outlined]="true"
                severity="secondary"
                size="small"
                [disabled]="true"
              ></p-button>
              <p-button
                label="Consultores Entrega"
                icon="pi pi-users"
                [outlined]="true"
                severity="secondary"
                size="small"
                [disabled]="true"
              ></p-button>
              <p-button
                label="Grabar"
                icon="pi pi-check"
                severity="success"
                size="small"
                (onClick)="grabarDetalle()"
                [disabled]="
                  !puedeGrabarDetalle() ||
                  detalleLoading() ||
                  cantidadCambiosPendientes() === 0
                "
              ></p-button>
            </div>

            <!-- Filtros en fieldset (incluye datos encabezado + filtros + buscar/limpiar) -->
            <p-fieldset
              legend="Detalle Servicio Entregado"
              [toggleable]="true"
              styleClass="mb-2"
            >
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Factura</label
                  >
                  <input
                    pInputText
                    [value]="facturaDetalle.cdfactura"
                    readonly
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Estado</label
                  >
                  <input
                    pInputText
                    [value]="facturaDetalle.estadoDescripcion"
                    readonly
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Consecutivo</label
                  >
                  <input
                    pInputText
                    [value]="facturaDetalle.nmconsecutivo"
                    readonly
                    class="w-full"
                  />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Cumplimiento</label
                  >
                  <input
                    pInputText
                    [(ngModel)]="filtroDetalle.nmconsecutivoCumplimiento"
                    type="number"
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Fecha Programación</label
                  >
                  <input
                    pInputText
                    [(ngModel)]="filtroDetalle.feprogramacion"
                    type="date"
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Municipio Entrega</label
                  >
                  <div class="flex gap-2">
                    <input
                      pInputText
                      [value]="municipioEntregaDetalleLabel()"
                      readonly
                      placeholder="Seleccione municipio"
                      class="w-full cursor-pointer"
                      (click)="abrirBuscadorMunicipioDetalle()"
                    />
                    <button
                      class="min-w-[44px] min-h-[44px]"
                      pButton
                      type="button"
                      icon="pi pi-search"
                      [outlined]="true"
                      severity="secondary"
                      pTooltip="Buscar municipio"
                      (click)="abrirBuscadorMunicipioDetalle()"
                    ></button>
                    @if (filtroDetalle.cdmunicipioEntrega) {
                      <button
                        class="min-w-[44px] min-h-[44px]"
                        pButton
                        type="button"
                        icon="pi pi-times"
                        [outlined]="true"
                        severity="secondary"
                        pTooltip="Limpiar municipio"
                        (click)="limpiarMunicipioDetalle()"
                      ></button>
                    }
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Asesor</label
                  >
                  <div class="flex gap-2">
                    <input
                      pInputText
                      [value]="asesorDetalleLabel()"
                      readonly
                      placeholder="Seleccione asesor"
                      class="w-full cursor-pointer"
                      (click)="mostrarBuscadorAsesorDetalle.set(true)"
                    />
                    <button
                      class="min-w-[44px] min-h-[44px]"
                      pButton
                      type="button"
                      icon="pi pi-search"
                      [outlined]="true"
                      severity="secondary"
                      pTooltip="Buscar asesor"
                      (click)="mostrarBuscadorAsesorDetalle.set(true)"
                    ></button>
                    @if (filtroDetalle.nmconsecutivoAsesor) {
                      <button
                        class="min-w-[44px] min-h-[44px]"
                        pButton
                        type="button"
                        icon="pi pi-times"
                        [outlined]="true"
                        severity="secondary"
                        pTooltip="Limpiar asesor"
                        (click)="limpiarAsesorDetalle()"
                      ></button>
                    }
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Beneficiario</label
                  >
                  <div class="flex gap-2">
                    <input
                      pInputText
                      [value]="beneficiarioDetalleLabel()"
                      readonly
                      placeholder="Seleccione beneficiario"
                      class="w-full cursor-pointer"
                      (click)="mostrarBuscadorBeneficiarioDetalle.set(true)"
                    />
                    <button
                      class="min-w-[44px] min-h-[44px]"
                      pButton
                      type="button"
                      icon="pi pi-search"
                      [outlined]="true"
                      severity="secondary"
                      pTooltip="Buscar beneficiario"
                      (click)="mostrarBuscadorBeneficiarioDetalle.set(true)"
                    ></button>
                    @if (filtroDetalle.nmconsecutivoBeneficiario) {
                      <button
                        class="min-w-[44px] min-h-[44px]"
                        pButton
                        type="button"
                        icon="pi pi-times"
                        [outlined]="true"
                        severity="secondary"
                        pTooltip="Limpiar beneficiario"
                        (click)="limpiarBeneficiarioDetalle()"
                      ></button>
                    }
                  </div>
                </div>
              </div>

              <app-buscador-municipio
                #buscadorMunicipioDetalle
                (municipioSeleccionado)="onMunicipioDetalleSeleccionado($event)"
              ></app-buscador-municipio>

              @if (mostrarBuscadorAsesorDetalle()) {
                <app-asesor-search
                  (onSelect)="onAsesorDetalleSeleccionado($event)"
                  (onClose)="mostrarBuscadorAsesorDetalle.set(false)"
                ></app-asesor-search>
              }

              @if (mostrarBuscadorBeneficiarioDetalle()) {
                <app-busqueda-cliente-dialog
                  [titulo]="'Buscar Beneficiario'"
                  (onSelect)="onBeneficiarioDetalleSeleccionado($event)"
                  (onClose)="mostrarBuscadorBeneficiarioDetalle.set(false)"
                ></app-busqueda-cliente-dialog>
              }

              <div class="flex flex-wrap justify-end gap-2">
                <button
                  pButton
                  type="button"
                  label="Buscar"
                  icon="pi pi-search"
                  (click)="buscarDetalleConFiltros()"
                ></button>
                <button
                  pButton
                  type="button"
                  label="Limpiar"
                  icon="pi pi-eraser"
                  severity="secondary"
                  [outlined]="true"
                  (click)="limpiarFiltrosDetalle()"
                ></button>
              </div>
            </p-fieldset>

            @if (!puedeGrabarDetalle()) {
              <div
                class="p-3 rounded border border-yellow-200 bg-yellow-50 text-yellow-700 text-sm"
              >
                {{ mensajeDetalleNoEditable() }}
              </div>
            }

            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="detallesFactura()"
              [rows]="detallePageSize()"
              responsiveLayout="scroll"
              [scrollable]="true"
              [tableStyle]="{ 'min-width': '126rem' }"
              styleClass="tabla-servicios-entregados-detalle p-datatable-sm p-datatable-gridlines"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn>Cumplimiento</th>
                  <th pResizableColumn>Asesor</th>
                  <th pResizableColumn>Cliente</th>
                  <th pResizableColumn>Servicio</th>
                  <th pResizableColumn>Clasificación</th>
                  <th pResizableColumn>Atención Virtual</th>
                  <th pResizableColumn>Tipo</th>
                  <th pResizableColumn>Fecha</th>
                  <th pResizableColumn>Cant</th>
                  <th pResizableColumn>Municipio Origen</th>
                  <th pResizableColumn>Municipio Entrega</th>
                  <th pResizableColumn>Valor Transporte</th>
                  <th pResizableColumn>Adic. Asesor</th>
                  <th pResizableColumn>Adic. Consultoría</th>
                  <th pResizableColumn>Viáticos</th>
                  <th pResizableColumn>Pago viáticos</th>
                  <th pResizableColumn>Proveedor Transporte</th>
                  <th pResizableColumn>ID Soporte BCD/SVC</th>
                  <th pResizableColumn>ID Transporte especial</th>
                  <th pResizableColumn>ID Vale de taxi</th>
                  <th pResizableColumn>Observaciones gastos de viaje</th>
                  <th pResizableColumn>Formato Cap.</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-detalle>
                <tr>
                  <td>{{ detalle.nmconsecutivoCumplimiento }}</td>
                  <td>{{ detalle.dsasesor }}</td>
                  <td>{{ detalle.dsbeneficiario }}</td>
                  <td>{{ detalle.dsservicio }}</td>
                  <td>{{ detalle.dsclasificacion }}</td>
                  <td>{{ textoAtencionVirtual(detalle) }}</td>
                  <td>
                    @if (esTipoInforme(detalle)) {
                      <i
                        class="pi pi-file text-blue-600"
                        pTooltip="Informe"
                      ></i>
                    } @else {
                      <i
                        class="pi pi-calendar text-green-600"
                        pTooltip="Campo"
                      ></i>
                    }
                  </td>
                  <td>
                    {{ formatFechaProgramacionDetalle(detalle.feprogramacion) }}
                  </td>
                  <td>
                    <input
                      pInputText
                      type="number"
                      step="0.01"
                      class="w-16 p-inputtext-sm text-xs"
                      [ngModel]="detalle.nmcantidadEjecutada"
                      (ngModelChange)="
                        onMontoDetalleChange(
                          detalle,
                          'nmcantidadEjecutada',
                          $event
                        )
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>{{ detalle.dsmunicipioOrigen }}</td>
                  <td>{{ detalle.dsmunicipioEntrega }}</td>
                  <td>
                    <input
                      pInputText
                      type="number"
                      class="w-20 p-inputtext-sm text-xs"
                      [ngModel]="detalle.ptvalorTransporte"
                      (ngModelChange)="
                        onMontoDetalleChange(
                          detalle,
                          'ptvalorTransporte',
                          $event
                        )
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      type="number"
                      class="w-20 p-inputtext-sm text-xs"
                      [ngModel]="detalle.ptvalorTransporteAdicional"
                      (ngModelChange)="
                        onMontoDetalleChange(
                          detalle,
                          'ptvalorTransporteAdicional',
                          $event
                        )
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      type="number"
                      class="w-20 p-inputtext-sm text-xs"
                      [ngModel]="detalle.ptvalorAdicionalCons"
                      (ngModelChange)="
                        onMontoDetalleChange(
                          detalle,
                          'ptvalorAdicionalCons',
                          $event
                        )
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      type="number"
                      class="w-20 p-inputtext-sm text-xs"
                      [ngModel]="detalle.ptvalorViaticos"
                      (ngModelChange)="
                        onMontoDetalleChange(detalle, 'ptvalorViaticos', $event)
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="detalle.snpagoViaticos === 'S'"
                      (ngModelChange)="onPagoViaticosChange(detalle, $event)"
                      [disabled]="!puedeEditarDetalle(detalle)"
                    ></p-checkbox>
                  </td>
                  <td>
                    <p-dropdown
                      [options]="transportesCatalogo()"
                      optionLabel="dsnombre"
                      optionValue="nmconsecutivo"
                      [filter]="true"
                      filterBy="dsnombre"
                      [showClear]="true"
                      [ngModel]="detalle.nmconsecutivoTransporteProveedor"
                      (ngModelChange)="
                        onProveedorTransporteChange(detalle, $event)
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                      appendTo="body"
                      scrollHeight="220px"
                      [panelStyle]="{ 'min-width': '22rem' }"
                      [style]="{
                        width: '100%',
                        'max-width': '100%',
                        height: '1.9rem'
                      }"
                      styleClass="w-full p-inputtext-sm text-xs"
                    ></p-dropdown>
                  </td>
                  <td>
                    <input
                      pInputText
                      class="w-full min-w-0 p-inputtext-sm text-xs"
                      [(ngModel)]="detalle.dsproveedorTransporte"
                      (input)="
                        onDetalleTextoInput(detalle, 'dsproveedorTransporte')
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      class="w-full min-w-0 p-inputtext-sm text-xs"
                      [(ngModel)]="detalle.dsespecialTransporte"
                      (input)="
                        onDetalleTextoInput(detalle, 'dsespecialTransporte')
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      class="w-full min-w-0 p-inputtext-sm text-xs"
                      [(ngModel)]="detalle.dsvaletaxiTransporte"
                      (input)="
                        onDetalleTextoInput(detalle, 'dsvaletaxiTransporte')
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    <input
                      pInputText
                      class="w-full min-w-0 p-inputtext-sm text-xs"
                      [(ngModel)]="detalle.dsobservacionViaticos"
                      (input)="
                        onDetalleTextoInput(detalle, 'dsobservacionViaticos')
                      "
                      [disabled]="!puedeEditarDetalle(detalle)"
                    />
                  </td>
                  <td>
                    @if (esFormatoCapacitacion(detalle)) {
                      <button
                        class="min-w-[44px] min-h-[44px] text-blue-600"
                        pButton
                        type="button"
                        icon="pi pi-search"
                        [text]="true"
                        pTooltip="Formato capacitación"
                        (click)="abrirFormatoCapacitacion(detalle)"
                      ></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>

            @if (detalleTotalRegistros() > 0) {
              <div
                class="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-3"
              >
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  Registros del {{ detalleRegistroInicio() }} al
                  {{ detalleRegistroFin() }} de {{ detalleTotalRegistros() }}
                </span>
                <div class="flex gap-2">
                  <button
                    class="min-w-[44px] min-h-[44px]"
                    pButton
                    icon="pi pi-angle-left"
                    pTooltip="Página anterior"
                    [disabled]="!detalleHasPrevious()"
                    (click)="irPaginaDetalleAnterior()"
                  ></button>
                  <button
                    class="min-w-[44px] min-h-[44px]"
                    pButton
                    icon="pi pi-angle-right"
                    pTooltip="Página siguiente"
                    [disabled]="!detalleHasNext()"
                    (click)="irPaginaDetalleSiguiente()"
                  ></button>
                </div>
              </div>
            }

            @if (cantidadCambiosPendientes() > 0) {
              <div
                class="p-3 rounded border border-blue-200 bg-blue-50 text-blue-700 text-sm"
              >
                Tiene {{ cantidadCambiosPendientes() }} registro(s) con cambios
                pendientes por grabar.
              </div>
            }
          </div>
        }
      </p-dialog>

      <app-facturas-resultados
        [facturas]="facturas()"
        [loading]="loading()"
        [totalRegistros]="totalRegistros()"
        [pageSize]="pageSize()"
        [currentPage]="currentPage()"
        [totalPages]="totalPages()"
        [registroInicio]="registroInicio()"
        [registroFin]="registroFin()"
        [hasPrevious]="hasPrevious()"
        [hasNext]="hasNext()"
        [pageSizeOptions]="pageSizeOptions"
        [selectedPageSize]="selectedPageSize"
        [mostrarAccionEditar]="mostrarAccionEditarFn"
        [mostrarAccionVerDetalle]="mostrarAccionVerDetalleFn"
        [mostrarAccionEliminar]="mostrarAccionEliminarFn"
        [mostrarAccionEnviarCliente]="mostrarAccionEnviarClienteFn"
        [mostrarAccionAgregarDetalle]="mostrarAccionAgregarDetalleFn"
        [labelAgregarDetalle]="labelAgregarDetalleFn"
        (editar)="abrirModificarEncabezado($event)"
        (verDetalle)="abrirVerDetalle($event)"
        (eliminar)="eliminarFactura($event)"
        (enviarCliente)="enviarFacturaCliente($event)"
        (agregarDetalle)="agregarDetalleFactura($event)"
        (selectedPageSizeChange)="onSelectedPageSizeChange($event)"
        (irPrimeraPagina)="irPrimeraPagina()"
        (irPaginaAnterior)="irPaginaAnterior()"
        (irPaginaSiguiente)="irPaginaSiguiente()"
        (irUltimaPagina)="irUltimaPagina()"
      ></app-facturas-resultados>

      <!-- Dialogo Rectificar Transporte -->
      @if (mostrarRectificarTransporte() && facturaSeleccionadaDetalle()) {
        <app-rectificar-transporte
          [nmconsecutivoFactura]="facturaSeleccionadaDetalle()!.nmconsecutivo"
          (cerrarDialog)="mostrarRectificarTransporte.set(false)"
          (recalcular)="onRecalcularPrefactura()"
        >
        </app-rectificar-transporte>
      }
    </div>
  `
})
export class ServiciosEntregadosComponent implements OnInit {
  private static readonly TIPO_CITA_INFORME = TIPO_CITA.INFORME;
  private static readonly FLAG_SI = YES_VALUE;
  private static readonly FLAG_NO = NO_VALUE;
  private static readonly CLASIFICACION_CAPACITACION = 'CAPACITACION';

  readonly facade = inject(ServiciosEntregadosFacade);

  mostrarNuevoEncabezadoFactura = signal(false);
  mostrarModificarEncabezadoFactura = signal(false);
  mostrarDetalleFactura = signal(false);
  mostrarBuscadorCliente = signal(false);
  mostrarBuscadorConvenio = signal(false);
  mostrarBuscadorAsesorDetalle = signal(false);
  mostrarBuscadorBeneficiarioDetalle = signal(false);
  facturaSeleccionadaEditar = signal<FacturaEncabezado | null>(null);
  facturaSeleccionadaDetalle = signal<FacturaEncabezado | null>(null);
  mostrarRectificarTransporte = signal(false);
  detallesFactura = signal<FacturaDetallePrefactura[]>([]);
  detallesFacturaOriginales = signal<Map<number, FacturaDetallePrefactura>>(
    new Map()
  );
  detallesFacturaPendientes = signal<Map<number, FacturaDetallePrefactura>>(
    new Map()
  );
  detalleLoading = signal(false);
  detalleCurrentPage = signal(1);
  detallePageSize = signal(10);
  detalleTotalRegistros = signal(0);
  detalleNextCursor = signal<string | undefined>(undefined);
  detallePreviousCursor = signal<string | undefined>(undefined);
  detalleHasNext = signal(false);
  detalleHasPrevious = signal(false);
  detalleCursorRequest = signal<string | null>(null);
  detalleDirectionRequest = signal<'NEXT' | 'PREVIOUS'>('NEXT');
  transportesCatalogo = signal<TransporteProveedor[]>([]);
  filtroDetalle: {
    nmconsecutivoCumplimiento?: number;
    feprogramacion?: string | Date;
    cdmunicipioEntrega?: string;
    nmconsecutivoAsesor?: number;
    nmconsecutivoBeneficiario?: number;
    snpagoTransporteEmpleado?: string;
  } = {};

  municipioEntregaDetalleLabel = signal('');
  asesorDetalleLabel = signal('');
  beneficiarioDetalleLabel = signal('');

  @ViewChild('buscadorMunicipioDetalle')
  buscadorMunicipioDetalle?: BuscadorMunicipioComponent;

  loading = this.facade.loading;
  facturas = this.facade.facturas;
  totalRegistros = this.facade.totalRegistros;
  currentPage = this.facade.currentPage;
  pageSize = this.facade.pageSize;
  oficinas = this.facade.oficinas;
  clienteCodigoLabel = this.facade.clienteCodigoLabel;
  clienteIdentificacionLabel = this.facade.clienteIdentificacionLabel;
  clienteNombreLabel = this.facade.clienteNombreLabel;
  mesesOptions = this.facade.mesesOptions;
  pageSizeOptions = this.facade.pageSizeOptions;
  filtro = this.facade.filtro;
  totalPages = this.facade.totalPages;
  registroInicio = this.facade.registroInicio;
  registroFin = this.facade.registroFin;
  hasPrevious = this.facade.hasPrevious;
  hasNext = this.facade.hasNext;

  private readonly confirmationService = inject(ConfirmationService);

  readonly mostrarAccionEditarFn = (factura: FacturaEncabezado): boolean =>
    this.mostrarAccionEditar(factura);
  readonly mostrarAccionVerDetalleFn = (factura: FacturaEncabezado): boolean =>
    this.mostrarAccionVerDetalle(factura);
  readonly mostrarAccionEliminarFn = (factura: FacturaEncabezado): boolean =>
    this.mostrarAccionEliminar(factura);
  readonly mostrarAccionEnviarClienteFn = (
    factura: FacturaEncabezado
  ): boolean => this.mostrarAccionEnviarCliente(factura);
  readonly mostrarAccionAgregarDetalleFn = (
    factura: FacturaEncabezado
  ): boolean => this.facade.mostrarAccionAgregarDetalle(factura);
  readonly labelAgregarDetalleFn = (factura: FacturaEncabezado): string =>
    this.facade.obtenerLabelAgregarDetalle(factura);

  get selectedPageSize(): number {
    return this.facade.selectedPageSize;
  }

  set selectedPageSize(value: number) {
    this.facade.selectedPageSize = value;
  }

  ngOnInit(): void {
    void this.facade.init();
  }

  buscar(): void {
    this.facade.buscar();
  }

  limpiarFiltros(): void {
    this.facade.limpiarFiltros();
  }

  abrirBuscadorCliente(): void {
    this.mostrarBuscadorCliente.set(true);
  }

  onCdFacturaInput(): void {
    this.facade.onCdFacturaInput();
  }

  onAnioInput(): void {
    this.facade.onAnioInput();
  }

  onAseguradoraSeleccionada(aseguradora: AseguradoraSeleccionada): void {
    this.facade.onAseguradoraSeleccionada(aseguradora);
    this.onCerrarBusquedaCliente();
  }

  onClienteSeleccionado(aseguradora: AseguradoraSeleccionada): void {
    this.onAseguradoraSeleccionada(aseguradora);
  }

  onCerrarBusquedaCliente(): void {
    this.mostrarBuscadorCliente.set(false);
  }

  limpiarCliente(): void {
    this.facade.limpiarCliente();
  }

  limpiarConvenio(): void {
    this.facade.limpiarConvenio();
  }

  abrirBuscadorConvenio(): void {
    this.mostrarBuscadorConvenio.set(true);
  }

  onConvenioSeleccionado(convenio: ConvenioSeleccionado): void {
    this.facade.onConvenioSeleccionado(convenio);
    this.mostrarBuscadorConvenio.set(false);
  }

  irPrimeraPagina(): void {
    this.facade.irPrimeraPagina();
  }

  irPaginaAnterior(): void {
    this.facade.irPaginaAnterior();
  }

  irPaginaSiguiente(): void {
    this.facade.irPaginaSiguiente();
  }

  irUltimaPagina(): void {
    this.facade.irUltimaPagina();
  }

  onPageSizeChange(): void {
    this.facade.onPageSizeChange();
  }

  onSelectedPageSizeChange(value: number): void {
    this.selectedPageSize = value;
    this.onPageSizeChange();
  }

  mostrarAccionEliminar(factura: FacturaEncabezado): boolean {
    return this.facade.mostrarAccionEliminar(factura);
  }

  mostrarAccionEnviarCliente(factura: FacturaEncabezado): boolean {
    return this.facade.mostrarAccionEnviarCliente(factura);
  }

  mostrarAccionEditar(factura: FacturaEncabezado): boolean {
    return this.facade.mostrarAccionEditar(factura);
  }

  mostrarAccionVerDetalle(factura: FacturaEncabezado): boolean {
    return this.facade.mostrarAccionVerDetalle(factura);
  }

  async abrirModificarEncabezado(factura: FacturaEncabezado): Promise<void> {
    const detalle = await this.facade.obtenerFacturaParaEditar(
      factura.nmconsecutivo
    );
    if (!detalle) {
      return;
    }

    this.facturaSeleccionadaEditar.set(detalle);
    this.mostrarModificarEncabezadoFactura.set(true);
  }

  async onEncabezadoCreado(encabezado: EncabezadoFactura): Promise<void> {
    if (!encabezado.nmconsecutivo) {
      return;
    }

    this.mostrarNuevoEncabezadoFactura.set(false);
    await this.abrirModificarEncabezado({
      nmconsecutivo: encabezado.nmconsecutivo
    } as FacturaEncabezado);
  }

  async abrirVerDetalle(factura: FacturaEncabezado): Promise<void> {
    const detalle = await this.facade.obtenerFacturaParaEditar(
      factura.nmconsecutivo
    );
    if (!detalle) {
      return;
    }

    this.facturaSeleccionadaDetalle.set(detalle);
    this.detalleCurrentPage.set(1);
    this.detalleCursorRequest.set(null);
    this.detalleDirectionRequest.set('NEXT');
    this.detalleNextCursor.set(undefined);
    this.detallePreviousCursor.set(undefined);
    this.detalleHasNext.set(false);
    this.detalleHasPrevious.set(false);
    this.detallesFacturaPendientes.set(new Map());
    await this.cargarCatalogoTransporte();
    this.mostrarDetalleFactura.set(true);
    await this.buscarDetalle();
  }

  maximizarDialogoDetalle(dialog: {
    maximized?: boolean;
    maximize?: (event?: Event) => void;
  }): void {
    if (dialog.maximize) {
      try {
        dialog.maximize(new Event('click'));
      } catch {
        // No-op: fallback below keeps the state maximized when API is available.
      }
    }

    if (dialog.maximized !== true) {
      dialog.maximized = true;
    }
  }

  cerrarDialogoDetalle(): void {
    this.mostrarDetalleFactura.set(false);
    this.facturaSeleccionadaDetalle.set(null);
    this.detallesFactura.set([]);
    this.detallesFacturaOriginales.set(new Map());
    this.detallesFacturaPendientes.set(new Map());
    this.detalleTotalRegistros.set(0);
    this.detalleNextCursor.set(undefined);
    this.detallePreviousCursor.set(undefined);
    this.detalleHasNext.set(false);
    this.detalleHasPrevious.set(false);
    this.detalleCursorRequest.set(null);
    this.detalleDirectionRequest.set('NEXT');
    this.transportesCatalogo.set([]);
    this.filtroDetalle = {};
  }

  intentarCerrarDialogoDetalle(): void {
    if (this.cantidadCambiosPendientes() === 0) {
      this.cerrarDialogoDetalle();
      return;
    }

    this.confirmationService.confirm({
      header: 'Cambios sin grabar',
      message:
        'Tiene cambios pendientes de grabar. Si cierra ahora, se perderán las modificaciones realizadas. ¿Desea cerrar sin grabar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Cerrar sin grabar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.cerrarDialogoDetalle();
      }
    });
  }

  irRectificarTransporte(): void {
    const factura = this.facturaSeleccionadaDetalle();
    if (!factura) return;
    this.mostrarRectificarTransporte.set(true);
  }

  onRecalcularPrefactura(): void {
    this.buscarDetalle();
  }

  async cargarCatalogoTransporte(): Promise<void> {
    try {
      const catalogo = await this.facade.listarCatalogoTransporte();
      this.transportesCatalogo.set(catalogo || []);
    } catch {
      this.transportesCatalogo.set([]);
    }
  }

  async buscarDetalle(): Promise<void> {
    const factura = this.facturaSeleccionadaDetalle();
    if (!factura) {
      return;
    }

    this.detalleLoading.set(true);
    try {
      const filtroDetalleNormalizado = {
        ...this.filtroDetalle,
        feprogramacion: this.normalizarFechaFiltroDetalle(
          this.filtroDetalle.feprogramacion
        )
      };

      const resultado = await this.facade.cargarDetalleFactura(
        factura.nmconsecutivo,
        filtroDetalleNormalizado,
        this.detallePageSize(),
        this.detalleCursorRequest(),
        this.detalleDirectionRequest()
      );
      const detallesBase = resultado.detalles || [];
      this.detallesFacturaOriginales.set(
        this.crearSnapshotDetalles(detallesBase)
      );
      this.detallesFactura.set(this.aplicarCambiosPendientes(detallesBase));
      this.detalleTotalRegistros.set(resultado.totalRegistros || 0);
      this.detalleNextCursor.set(resultado.nextCursor);
      this.detallePreviousCursor.set(resultado.previousCursor);
      this.detalleHasNext.set(!!resultado.hasNext);
      this.detalleHasPrevious.set(!!resultado.hasPrevious);
    } finally {
      this.detalleLoading.set(false);
    }
  }

  buscarDetalleConFiltros(): void {
    // Legacy: al buscar en detalle, siempre vuelve a la primera página.
    this.detalleCurrentPage.set(1);
    this.detalleCursorRequest.set(null);
    this.detalleDirectionRequest.set('NEXT');
    void this.buscarDetalle();
  }

  limpiarFiltrosDetalle(): void {
    this.filtroDetalle = {};
    this.municipioEntregaDetalleLabel.set('');
    this.asesorDetalleLabel.set('');
    this.beneficiarioDetalleLabel.set('');
    this.detalleCurrentPage.set(1);
    this.detalleCursorRequest.set(null);
    this.detalleDirectionRequest.set('NEXT');
    void this.buscarDetalle();
  }

  abrirBuscadorMunicipioDetalle(): void {
    this.buscadorMunicipioDetalle?.abrir();
  }

  onMunicipioDetalleSeleccionado(municipio: Municipio): void {
    this.filtroDetalle.cdmunicipioEntrega = municipio.cdmunicipio;
    this.municipioEntregaDetalleLabel.set(
      [municipio.cdmunicipio, municipio.dsmunicipio].filter(Boolean).join(' - ')
    );
  }

  onAsesorDetalleSeleccionado(asesor: AsesorSeleccionado): void {
    const nmconsecutivoAsesor = Number(
      asesor.nmconsecutivo ?? asesor.cdpersona
    );
    this.filtroDetalle.nmconsecutivoAsesor = Number.isFinite(
      nmconsecutivoAsesor
    )
      ? nmconsecutivoAsesor
      : undefined;

    const nombre = [
      asesor.dsnombre1,
      asesor.dsnombre2,
      asesor.dsapellido1,
      asesor.dsapellido2
    ]
      .filter((parte) => !!parte)
      .join(' ')
      .trim();
    this.asesorDetalleLabel.set(
      [asesor.nmidentificacion, nombre].filter(Boolean).join(' - ')
    );
    this.mostrarBuscadorAsesorDetalle.set(false);
  }

  onBeneficiarioDetalleSeleccionado(beneficiario: ClienteSeleccionado): void {
    this.filtroDetalle.nmconsecutivoBeneficiario = beneficiario.nmconsecutivo;
    this.beneficiarioDetalleLabel.set(
      [beneficiario.numeroIdentificacion, beneficiario.nombreComercial]
        .filter(Boolean)
        .join(' - ')
    );
    this.mostrarBuscadorBeneficiarioDetalle.set(false);
  }

  limpiarMunicipioDetalle(): void {
    this.filtroDetalle.cdmunicipioEntrega = undefined;
    this.municipioEntregaDetalleLabel.set('');
  }

  limpiarAsesorDetalle(): void {
    this.filtroDetalle.nmconsecutivoAsesor = undefined;
    this.asesorDetalleLabel.set('');
  }

  limpiarBeneficiarioDetalle(): void {
    this.filtroDetalle.nmconsecutivoBeneficiario = undefined;
    this.beneficiarioDetalleLabel.set('');
  }

  detalleTotalPaginas(): number {
    const total = this.detalleTotalRegistros();
    const size = this.detallePageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  }

  detalleRegistroInicio(): number {
    if (this.detalleTotalRegistros() === 0) {
      return 0;
    }
    return (this.detalleCurrentPage() - 1) * this.detallePageSize() + 1;
  }

  detalleRegistroFin(): number {
    if (this.detalleTotalRegistros() === 0) {
      return 0;
    }
    return Math.min(
      this.detalleRegistroInicio() + this.detallePageSize() - 1,
      this.detalleTotalRegistros()
    );
  }

  irPaginaDetalleAnterior(): void {
    if (!this.detalleHasPrevious()) {
      return;
    }

    const cursor = this.detallePreviousCursor();
    if (!cursor) {
      return;
    }

    this.detalleCursorRequest.set(cursor);
    this.detalleDirectionRequest.set('PREVIOUS');
    this.detalleCurrentPage.update((page) => page - 1);
    void this.buscarDetalle();
  }

  irPaginaDetalleSiguiente(): void {
    if (!this.detalleHasNext()) {
      return;
    }

    const cursor = this.detalleNextCursor();
    if (!cursor) {
      return;
    }

    this.detalleCursorRequest.set(cursor);
    this.detalleDirectionRequest.set('NEXT');
    this.detalleCurrentPage.update((page) => page + 1);
    void this.buscarDetalle();
  }

  puedeEditarDetalle(detalle: FacturaDetallePrefactura): boolean {
    return this.puedeGrabarDetalle() && !this.esDetalleBloqueado(detalle);
  }

  esDetalleBloqueado(detalle: FacturaDetallePrefactura): boolean {
    return this.esTipoInforme(detalle) || this.esAtencionVirtual(detalle);
  }

  esTipoInforme(detalle: FacturaDetallePrefactura): boolean {
    if (
      detalle.nmconsecutivoTipoCita ===
      ServiciosEntregadosComponent.TIPO_CITA_INFORME
    ) {
      return true;
    }

    const tipoInforme = (detalle.sntipoInforme || '').trim().toUpperCase();
    if (tipoInforme) {
      return tipoInforme !== 'N';
    }

    return (detalle.dstipo || '').toUpperCase().includes('INFORME');
  }

  textoAtencionVirtual(detalle: FacturaDetallePrefactura): string {
    return this.esAtencionVirtual(detalle) ? 'SI' : 'NO';
  }

  esFormatoCapacitacion(detalle: FacturaDetallePrefactura): boolean {
    return (detalle.dsclasificacion || '')
      .toUpperCase()
      .includes(ServiciosEntregadosComponent.CLASIFICACION_CAPACITACION);
  }

  abrirFormatoCapacitacion(detalle: FacturaDetallePrefactura): void {
    const nmconsecutivoCumplimiento = detalle.nmconsecutivoCumplimiento;
    if (!nmconsecutivoCumplimiento || !this.esFormatoCapacitacion(detalle)) {
      return;
    }

    const url = `${environment.backend.baseUrl}/servicios/orientacionesProgramadas/${nmconsecutivoCumplimiento}/descargar-acta`;
    window.open(url, '_blank', 'noopener');
  }

  onMontoDetalleChange(
    detalle: FacturaDetallePrefactura,
    campo: CampoNumericoDetalle,
    valor: unknown
  ): void {
    detalle[campo] =
      campo === 'nmcantidadEjecutada'
        ? this.normalizarCantidadDetalle(valor)
        : this.normalizarNumeroDetalle(valor);
    this.sincronizarCambioPendiente(detalle);
  }

  onPagoViaticosChange(
    detalle: FacturaDetallePrefactura,
    checked: boolean
  ): void {
    detalle.snpagoViaticos = checked
      ? ServiciosEntregadosComponent.FLAG_SI
      : ServiciosEntregadosComponent.FLAG_NO;
    this.sincronizarCambioPendiente(detalle);
  }

  onDetalleTextoInput(
    detalle: FacturaDetallePrefactura,
    campo: CampoTextoDetalle
  ): void {
    detalle[campo] = sanitizeText(detalle[campo] || '');
    this.sincronizarCambioPendiente(detalle);
  }

  onProveedorTransporteChange(
    detalle: FacturaDetallePrefactura,
    nmconsecutivo: number | null
  ): void {
    detalle.nmconsecutivoTransporteProveedor = nmconsecutivo ?? undefined;

    const seleccionado = this.transportesCatalogo().find(
      (item) => item.nmconsecutivo === detalle.nmconsecutivoTransporteProveedor
    );
    detalle.dstipoTransporte = seleccionado?.dsnombre;
    this.sincronizarCambioPendiente(detalle);
  }

  private esAtencionVirtual(detalle: FacturaDetallePrefactura): boolean {
    return (
      (detalle.snatencionVirtual || '').trim().toUpperCase() ===
      ServiciosEntregadosComponent.FLAG_SI
    );
  }

  private normalizarNumeroDetalle(valor: unknown): number | undefined {
    if (valor === null || valor === undefined || valor === '') {
      return undefined;
    }

    const soloDigitos = String(valor).replace(/\D/g, '').slice(0, 9);
    if (!soloDigitos) {
      return undefined;
    }

    return Number(soloDigitos);
  }

  private normalizarCantidadDetalle(valor: unknown): number | undefined {
    if (valor === null || valor === undefined || valor === '') {
      return undefined;
    }

    const textoNormalizado = String(valor)
      .trim()
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    if (!textoNormalizado) {
      return undefined;
    }

    const [parteEnteraRaw = '', ...parteDecimalRaw] =
      textoNormalizado.split('.');
    const parteEntera = parteEnteraRaw.replace(/\D/g, '').slice(0, 9);
    const parteDecimal = parteDecimalRaw
      .join('')
      .replace(/\D/g, '')
      .slice(0, 2);
    const numeroTexto = parteDecimal
      ? `${parteEntera || '0'}.${parteDecimal}`
      : parteEntera;

    if (!numeroTexto) {
      return undefined;
    }

    const numero = Number(numeroTexto);
    return Number.isFinite(numero) ? numero : undefined;
  }

  puedeGrabarDetalle(): boolean {
    const factura = this.facturaSeleccionadaDetalle();
    if (!factura) {
      return false;
    }
    return this.facade.puedeGrabarDetalle(factura.cdestado);
  }

  mensajeDetalleNoEditable(): string {
    const factura = this.facturaSeleccionadaDetalle();
    return this.facade.obtenerMensajeDetalleNoEditable(factura?.cdestado);
  }

  async grabarDetalle(): Promise<void> {
    const factura = this.facturaSeleccionadaDetalle();
    if (!factura || !this.puedeGrabarDetalle()) {
      return;
    }

    const detallesModificados = Array.from(
      this.detallesFacturaPendientes().values()
    );
    if (detallesModificados.length === 0) {
      return;
    }

    this.detalleLoading.set(true);
    try {
      await this.facade.guardarDetalleFactura(
        factura.nmconsecutivo,
        detallesModificados
      );
      this.detallesFacturaPendientes.set(new Map());
      await this.buscarDetalle();
    } catch {
      // El facade se encarga de notificar el error
    } finally {
      this.detalleLoading.set(false);
    }
  }

  cerrarDialogoModificar(): void {
    this.mostrarModificarEncabezadoFactura.set(false);
    this.facturaSeleccionadaEditar.set(null);
  }

  async guardarEdicion(
    payload: ActualizarEncabezadoFacturaPayload
  ): Promise<void> {
    const facturaActual = this.facturaSeleccionadaEditar();
    if (!facturaActual) {
      return;
    }

    const actualizado = await this.facade.actualizarEncabezado(
      facturaActual,
      payload
    );
    if (actualizado) {
      this.cerrarDialogoModificar();
    }
  }

  async onValidarNombreEdicion(
    event: ValidarNombreFacturaEvent
  ): Promise<void> {
    const existe = await this.facade.validarNombreEnEdicion(
      event.cdfactura,
      event.nmconsecutivo
    );
    if (!existe) {
      return;
    }

    const actual = this.facturaSeleccionadaEditar();
    if (!actual) {
      return;
    }

    this.facturaSeleccionadaEditar.set({
      ...actual,
      cdfactura: event.previousCdfactura
    });
  }

  eliminarFactura(factura: FacturaEncabezado): void {
    this.facade.eliminarFactura(factura);
  }

  enviarFacturaCliente(factura: FacturaEncabezado): void {
    this.facade.enviarFacturaCliente(factura);
  }

  agregarDetalleFactura(factura: FacturaEncabezado): void {
    this.facade.agregarDetalleFactura(factura);
  }

  private crearSnapshotDetalles(
    detalles: FacturaDetallePrefactura[]
  ): Map<number, FacturaDetallePrefactura> {
    return new Map(
      detalles.map((detalle) => [detalle.nmconsecutivo, { ...detalle }])
    );
  }

  cantidadCambiosPendientes(): number {
    return this.detallesFacturaPendientes().size;
  }

  private aplicarCambiosPendientes(
    detalles: FacturaDetallePrefactura[]
  ): FacturaDetallePrefactura[] {
    const pendientes = this.detallesFacturaPendientes();
    return detalles.map((detalle) => {
      const pendiente = pendientes.get(detalle.nmconsecutivo);
      return pendiente ? { ...detalle, ...pendiente } : detalle;
    });
  }

  private sincronizarCambioPendiente(detalle: FacturaDetallePrefactura): void {
    const original = this.detallesFacturaOriginales().get(
      detalle.nmconsecutivo
    );
    if (!original) {
      return;
    }

    const pendientes = new Map(this.detallesFacturaPendientes());
    if (this.esDetalleDistinto(detalle, original)) {
      pendientes.set(detalle.nmconsecutivo, { ...detalle });
    } else {
      pendientes.delete(detalle.nmconsecutivo);
    }

    this.detallesFacturaPendientes.set(pendientes);
  }

  private esDetalleDistinto(
    detalle: FacturaDetallePrefactura,
    original: FacturaDetallePrefactura
  ): boolean {
    return (
      detalle.nmcantidadEjecutada !== original.nmcantidadEjecutada ||
      detalle.ptvalorTransporte !== original.ptvalorTransporte ||
      detalle.ptvalorTransporteAdicional !==
        original.ptvalorTransporteAdicional ||
      detalle.ptvalorAdicionalCons !== original.ptvalorAdicionalCons ||
      detalle.ptvalorViaticos !== original.ptvalorViaticos ||
      detalle.snpagoViaticos !== original.snpagoViaticos ||
      detalle.nmconsecutivoTransporteProveedor !==
        original.nmconsecutivoTransporteProveedor ||
      (detalle.dsproveedorTransporte || '') !==
        (original.dsproveedorTransporte || '') ||
      (detalle.dsespecialTransporte || '') !==
        (original.dsespecialTransporte || '') ||
      (detalle.dsvaletaxiTransporte || '') !==
        (original.dsvaletaxiTransporte || '') ||
      (detalle.dsobservacionViaticos || '') !==
        (original.dsobservacionViaticos || '')
    );
  }

  formatFechaProgramacionDetalle(fecha: string | undefined): string {
    if (!fecha) {
      return '';
    }

    const normalizada = fecha.trim();
    if (!normalizada) {
      return '';
    }

    const [fechaParte, horaParteRaw] = normalizada.split('T');
    const horaParte = (horaParteRaw || '').substring(0, 5);
    const [anio, mes, dia] = (fechaParte || '').split('-');

    if (!anio || !mes || !dia) {
      return normalizada;
    }

    return `${dia}/${mes}/${anio} ${horaParte || '00:00'}`;
  }

  private normalizarFechaFiltroDetalle(
    fecha: string | Date | undefined
  ): string | undefined {
    if (!fecha) {
      return undefined;
    }

    if (fecha instanceof Date) {
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, '0');
      const day = String(fecha.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const value = String(fecha).trim();
    if (!value) {
      return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const onlyDate = value.split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(onlyDate) ? onlyDate : undefined;
  }
}
